import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

// Item 19: per-user accessibility preferences applied as <html data-a11y-*> attributes
// (see index.css) — contrast, motion, text size, chart data tables, chart patterns, focus ring.
export const A11Y_DEFAULT = { contrast: "auto", motion: "auto", text: "normal", chart_tables: false, chart_patterns: false, focus_ring: true };

const mq = (q) => (typeof window !== "undefined" && window.matchMedia ? window.matchMedia(q).matches : false);

export function applyA11y(prefs) {
  const p = { ...A11Y_DEFAULT, ...(prefs || {}) };
  if (typeof document === "undefined") return p;
  const root = document.documentElement;
  const contrast = p.contrast === "auto" ? (mq("(prefers-contrast: more)") ? "high" : "normal") : p.contrast;
  const motion = p.motion === "auto" ? (mq("(prefers-reduced-motion: reduce)") ? "reduce" : "allow") : p.motion;
  root.setAttribute("data-a11y-contrast", contrast);
  root.setAttribute("data-a11y-motion", motion);
  root.setAttribute("data-a11y-text", p.text);
  root.setAttribute("data-a11y-patterns", p.chart_patterns ? "on" : "off");
  root.setAttribute("data-a11y-focus", p.focus_ring ? "on" : "off");
  return p;
}

const A11yContext = createContext({ prefs: A11Y_DEFAULT, setPrefs: () => {}, reload: () => {} });

export function A11yProvider({ children }) {
  const { user } = useAuth();
  const [prefs, setPrefsState] = useState(() => applyA11y(null));
  const reload = useCallback(async () => {
    if (!user) { setPrefsState(applyA11y(null)); return; }
    try { const { data } = await api.get("/a11y/me"); setPrefsState(applyA11y(data.prefs)); }
    catch { setPrefsState(applyA11y(null)); }
  }, [user]);
  useEffect(() => { reload(); }, [reload]);
  const setPrefs = useCallback((p) => setPrefsState(applyA11y(p)), []);
  const value = useMemo(() => ({ prefs, setPrefs, reload }), [prefs, setPrefs, reload]);
  return <A11yContext.Provider value={value}>{children}</A11yContext.Provider>;
}

export const useA11y = () => useContext(A11yContext);
